import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, CheckCircle, Hourglass, Lock } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const COMMENT_MAX = 1000;
const RATING_LABELS = {
  1: "Péssimo",
  2: "Ruim",
  3: "Regular",
  4: "Bom",
  5: "Excelente",
};

function StarRow({ value, hover, onSelect, onHover, disabled }) {
  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onSelect(n)}
            onMouseEnter={() => !disabled && onHover(n)}
            onMouseLeave={() => !disabled && onHover(0)}
            aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
            data-testid={`review-star-${n}`}
            className={`p-2 sm:p-3 rounded-full transition-all duration-150 ${
              disabled
                ? "cursor-not-allowed"
                : "hover:scale-110 active:scale-95 cursor-pointer"
            }`}
          >
            <Star
              size={44}
              weight={filled ? "fill" : "regular"}
              className={`transition-colors duration-150 ${
                filled ? "text-amber-400" : "text-slate-300"
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function PublicReview() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/r/${token}`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        if (r.data.current_rating) setRating(r.data.current_rating);
        if (r.data.current_comment) setComment(r.data.current_comment);
      })
      .catch((e) => {
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 404) setError("not_found");
        else setError("load_error");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.post(`/r/${token}/rate`, {
        rating,
        comment: comment.trim() || null,
      });
      setSubmitted(true);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) setSubmitError("Avaliação já registrada. Não é possível alterar depois de 10 minutos.");
      else if (status === 410) setSubmitError("Este link expirou. Entre em contato com a oficina.");
      else if (status === 400 && detail === "comment_too_long") setSubmitError("Comentário muito longo.");
      else setSubmitError("Não foi possível enviar agora. Tente novamente em alguns segundos.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="text-center text-slate-400 text-sm py-12" data-testid="review-loading">
          Carregando…
        </div>
      </Shell>
    );
  }

  if (error === "not_found") {
    return (
      <Shell>
        <StateScreen
          icon={Hourglass}
          title="Link não encontrado"
          message="Esta avaliação não existe ou já foi removida."
          testId="review-not-found"
        />
      </Shell>
    );
  }

  if (data?.expired) {
    return (
      <Shell>
        <StateScreen
          icon={Hourglass}
          title="Esta avaliação expirou"
          message="O período para responder esta avaliação acabou. Caso queira deixar um feedback, entre em contato direto com a oficina."
          testId="review-expired"
        />
      </Shell>
    );
  }

  if (submitted || (data?.already_rated && !data?.can_edit)) {
    return (
      <Shell>
        <StateScreen
          icon={CheckCircle}
          iconClass="text-emerald-500"
          title="Obrigado pelo seu feedback! ⭐"
          message={
            data?.first_name
              ? `${data.first_name}, recebemos sua avaliação. Isso ajuda a ${data.company_name} a melhorar cada vez mais.`
              : "Sua avaliação ajuda a oficina a melhorar cada vez mais."
          }
          testId="review-thanks"
        />
      </Shell>
    );
  }

  const greeting = data?.first_name
    ? `Olá, ${data.first_name}!`
    : "Olá!";

  return (
    <Shell>
      <div data-testid="review-form">
        <p className="text-[13px] text-slate-500 text-center">{greeting}</p>
        <h1 className="text-[26px] sm:text-[28px] font-semibold tracking-tight text-slate-900 text-center mt-2 leading-tight">
          Como foi seu atendimento na <span className="text-slate-700">{data?.company_name}</span>?
        </h1>
        {data?.vehicle_label && (
          <p className="text-[12.5px] text-slate-500 text-center mt-2">
            Sobre o serviço no <strong className="text-slate-700">{data.vehicle_label}</strong>
          </p>
        )}

        <div className="mt-10">
          <StarRow
            value={rating}
            hover={hover}
            onSelect={setRating}
            onHover={setHover}
            disabled={submitting}
          />
          <p className="text-center text-[13px] text-slate-600 mt-4 h-5 transition-opacity" data-testid="review-rating-label">
            {(hover || rating) ? RATING_LABELS[hover || rating] : "Toque em uma estrela"}
          </p>
        </div>

        <div className="mt-8">
          <label className="text-[12px] text-slate-500 font-medium uppercase tracking-[0.08em] block mb-2">
            Quer deixar um comentário? (opcional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
            disabled={submitting}
            rows={4}
            data-testid="review-comment"
            placeholder="Ex: O atendimento foi rápido e o serviço ficou impecável."
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors resize-none"
          />
          <div className="text-right text-[11px] text-slate-400 mt-1 tabular-nums">
            {comment.length}/{COMMENT_MAX}
          </div>
        </div>

        {submitError && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12.5px] text-rose-700" data-testid="review-error">
            {submitError}
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!rating || submitting}
          data-testid="review-submit"
          className={`mt-8 w-full rounded-full py-3.5 text-[15px] font-semibold tracking-tight transition-all ${
            rating && !submitting
              ? "bg-slate-900 text-white hover:bg-slate-800 active:scale-[0.99]"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
        >
          {submitting ? "Enviando…" : "Enviar avaliação"}
        </button>

        {data?.already_rated && data?.can_edit && (
          <p className="text-center text-[11.5px] text-slate-400 mt-4 flex items-center justify-center gap-1.5" data-testid="review-edit-window">
            <Lock size={11} weight="regular" />
            Você pode ajustar sua avaliação por mais 10 minutos.
          </p>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-[0_8px_40px_-12px_rgba(15,23,42,0.15)] border border-slate-100 p-6 sm:p-9">
        {children}
        <div className="mt-10 pt-6 border-t border-slate-100 text-center">
          <p className="text-[10.5px] text-slate-300 tracking-[0.12em] uppercase">
            Powered by AgentUS
          </p>
        </div>
      </div>
    </div>
  );
}

function StateScreen({ icon: Icon, iconClass = "text-slate-400", title, message, testId }) {
  return (
    <div className="text-center py-6" data-testid={testId}>
      <Icon size={48} weight="regular" className={`mx-auto ${iconClass}`} />
      <h1 className="text-[22px] font-semibold tracking-tight text-slate-900 mt-5">
        {title}
      </h1>
      <p className="text-[13.5px] text-slate-500 mt-3 leading-relaxed max-w-sm mx-auto">
        {message}
      </p>
    </div>
  );
}
